import styles from './index.module.css'

import DiscoBolt from './assets/disco-bolt.svg'

import { IoArrowUp } from '@react-icons/all-files/io5/IoArrowUp'
import { IoClose } from '@react-icons/all-files/io5/IoClose'
import { SiApplemusic } from '@react-icons/all-files/si/SiApplemusic'
import { SiSpotify } from '@react-icons/all-files/si/SiSpotify'
import { SiYoutube } from '@react-icons/all-files/si/SiYoutube'
import { Box, Button, Flex, IconButton, Text, TextArea } from '@radix-ui/themes'
import React, { useEffect, useRef, useState } from 'react'

import { useTranslation } from '@disco/i18n/use-translation'

type Props = {
  value: string
  onChange: (value: string) => void
  onClear: () => void
  onSubmit: () => void
  // Example prompts shown while the box is empty; pass none to hide them
  suggestions?: readonly string[]
  rows?: number
}

const OVERLAY_ANIMATION_DURATION = 2400

/**
 * Prompt editor that opens over a search input once AI search is switched on.
 * Used by the in-app search bar, Browse and the catalog header; callers own
 * the prompt text and decide what a submit means.
 */
const AiSearchOverlay = ({
  value,
  onChange,
  onClear,
  onSubmit,
  suggestions,
  rows = 3,
}: Props): React.ReactNode => {
  const { t } = useTranslation('catalogs')
  const [rainbowDone, setRainbowDone] = useState(false)
  const hasText = value.trim().length > 0
  const promptSuggestions = suggestions ?? [
    t(
      'catalogs:aiPromptSuggestionWorkout',
      'Upbeat tracks with female vocals for a workout playlist',
    ),
    t(
      'catalogs:aiPromptSuggestionCinematic',
      'Cinematic orchestral pieces for dramatic scenes',
    ),
    t(
      'catalogs:aiPromptSuggestionLofi',
      'Lo-fi hip hop with a melancholic late-night mood',
    ),
  ]

  // Move caret to end on first focus, but not on subsequent focuses
  const caretMovedRef = useRef(false)
  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    if (caretMovedRef.current) return
    caretMovedRef.current = true
    const end = e.currentTarget.value.length
    e.currentTarget.setSelectionRange(end, end)
  }

  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    const el = textAreaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [value])

  // Use timeout instead of onAnimationEnd — CSS Modules hash keyframe names
  useEffect(() => {
    const timer = setTimeout(
      () => setRainbowDone(true),
      OVERLAY_ANIMATION_DURATION,
    )
    return () => clearTimeout(timer)
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      // Callers decide what an empty submit means; Browse clears its search
      onSubmit()
    }
  }

  return (
    <Box className={styles.overlay}>
      <Flex direction="column" gap="3" className={styles.expanded}>
        <Box
          className={`${styles.inputWrapper} ${
            rainbowDone ? styles.inputWrapperDone : ''
          }`}
        >
          <Box className={styles.inputInner}>
            {hasText && (
              <IconButton
                variant="ghost"
                size="1"
                radius="full"
                className={styles.closeBtn}
                onClick={onClear}
                aria-label={t('catalogs:clearPrompt', 'Clear prompt')}
              >
                <IoClose size={16} />
              </IconButton>
            )}
            <TextArea
              ref={textAreaRef}
              className={styles.textarea}
              value={value}
              onChange={e => onChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              placeholder={t(
                'catalogs:aiSearchPromptPlaceholder',
                'AI Search works best when you describe the feel or scene. Try adding mood, tempo or a reference. For example, "I need music that feels like the Stranger Things opening credits: slow, pulsing, ominous."',
              )}
              aria-label={t('catalogs:aiSearchPrompt', 'AI search prompt')}
              rows={rows}
              autoFocus
            />
            <IconButton
              variant="solid"
              size="2"
              radius="full"
              className={styles.sendBtn}
              disabled={!hasText}
              onClick={onSubmit}
              aria-label={t('catalogs:send', 'Send')}
            >
              <IoArrowUp size={16} />
            </IconButton>
          </Box>
        </Box>

        {!hasText && promptSuggestions.length > 0 && (
          <Flex wrap="wrap" gap="2" className={styles.suggestions}>
            {promptSuggestions.map(s => (
              <Button
                key={s}
                variant="soft"
                size="1"
                className={styles.suggestionChip}
                onClick={() => onChange(s)}
              >
                <Text size="1">{s}</Text>
              </Button>
            ))}
          </Flex>
        )}

        {!hasText && (
          <Flex align="center" gap="2" className={styles.linkRow}>
            <Text size="2" color="gray">
              {t(
                'catalogs:youCanAlsoPasteLinkFrom',
                'You can also paste a link from',
              )}
            </Text>
            <DiscoBolt width={16} height={16} className={styles.logoDisco} />
            <SiSpotify size={16} className={styles.logoSpotify} />
            <SiYoutube size={16} className={styles.logoYoutube} />
            <SiApplemusic size={16} className={styles.logoApple} />
          </Flex>
        )}
      </Flex>
    </Box>
  )
}

export default AiSearchOverlay
